"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";

const PAGE_SIZE = 12;

export default function InfiniteBlogGrid({ posts = [], pageSize = PAGE_SIZE }) {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [posts, pageSize]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (visibleCount >= posts.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((count) => Math.min(count + pageSize, posts.length));
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [posts.length, visibleCount, pageSize]);

  const visiblePosts = posts.slice(0, visibleCount);
  const hasMore = visibleCount < posts.length;

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {visiblePosts.map((post) => {
          const displayDate = post.date ? String(post.date).split(" ")[0] : "Date TBA";

          return (
            <article
              key={post.slug}
              className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-[#f7fcf8] via-white to-[#eef7ff] shadow-md transition duration-200 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-xl"
            >
              <Link
                href={`/blog/${post.slug}/`}
                className="relative block w-full overflow-hidden border-b border-slate-200 bg-gradient-to-br from-[#9fce8a]/10 via-white to-[#01A6DE]/10 group-hover:border-emerald-300 transition duration-200"
                aria-label={`Read blog post: ${post.title}`}
              >
                <div className="aspect-[16/10] w-full">
                  {post.image ? (
                    <img
                      src={post.image}
                      alt={post.title}
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-r from-[#9fce8a]/30 to-[#01A6DE]/20" />
                  )}
                </div>
                {post.image && (
                  <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent opacity-60"
                    aria-hidden="true"
                  />
                )}
              </Link>
              <div className="flex flex-1 flex-col gap-3 p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.12em] text-[#2f4f2f]">
                  {displayDate}
                </div>
                <h2 className="text-lg font-bold leading-snug text-[#1f3d1f]">
                  <Link href={`/blog/${post.slug}/`} className="transition-colors hover:text-[#0369a1]">
                    {post.title}
                  </Link>
                </h2>
                <p className="line-clamp-3 text-sm text-[#3f3f3f]">{post.excerpt}</p>
                <div className="mt-auto">
                  <Link
                    href={`/blog/${post.slug}/`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#0369a1]"
                  >
                    Read more
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div ref={sentinelRef} className="h-10" aria-hidden="true" />

      {hasMore && (
        <div className="py-6 text-center text-sm text-gray-500">
          Loading more posts...
        </div>
      )}
    </>
  );
}
